import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Header,
  Input,
  Popover,
  Select,
  SpaceBetween,
  Spinner,
  Table,
} from '@amzn/awsui-components-react-v3';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import { EmptyState } from 'src/commons/EmptyState';
import { getClassification, updateClassification } from 'src/api/classify';
import * as classify from '@amzn/awsdataplatform-classify-client';
import { listTemplates } from 'src/api/permissions';
import { TEMPLATE_TYPE_ACCESS_MANAGEMENT } from 'src/commons/constants';

const transformUIToAPIFormat = (input: any): classify.UpdateClassificationCommandInput => {
  if (input.columns) {
    const transformedColumns: classify.ColumnClassificationUpdate[] = input.columns.map((col: any) => {
      const apiCol: any = { columnName: col.columnName };

      if (col.overriddenClassification) {
        apiCol.overriddenClassification = col.overriddenClassification;
      }

      if (col.overrideComment) {
        apiCol.overrideComment = col.overrideComment;
      }

      return apiCol;
    });

    return {
      datasetId: input.datasetId,
      columns: transformedColumns,
    };
  }

  return {
    datasetId: input.datasetId,
    columns: [],
  };
};

const mapAPIResponseToUI = (apiResponse: classify.GetClassificationCommandOutput) => {
  const datasetClassification = apiResponse.datasetClassification;
  const columns = datasetClassification.columns || [];

  const finalDatasetClassification =
    datasetClassification.overriddenClassification || datasetClassification.classification;
  const hasPersonalData = finalDatasetClassification === 'PERSONAL';

  return {
    columns: columns.map((col: classify.ColumnClassification) => {
      const finalClassification =
        col.overriddenClassification !== undefined ? col.overriddenClassification : col.classification;

      return {
        columnName: col.columnName,
        classification: col.classification === 'PERSONAL' ? 'Personal Data' : 'Not Personal Data',
        overrideValue: finalClassification === 'PERSONAL' ? 'Personal Data' : 'Not Personal Data',
        overrideReasonValue: col.overrideComment || col.comment || '',
        customReasonText: '',
        comment: col.comment || '',
        effectiveClassification: finalClassification === 'PERSONAL' ? 'Personal Data' : 'Not Personal Data',
      };
    }),
    reviewStatus: datasetClassification.reviewStatus || 'PENDING',
    datasetClassification: hasPersonalData ? 'Personal Data' : 'Non-Personal Data',
    validationCheckPassed: !hasPersonalData,
    validationCheckMessage: hasPersonalData
      ? 'This dataset may contain Personal Data and failed validation checks for Personal Data. Select "Accept" if this dataset contains Personal Data, then associate template and revalidate checks.'
      : 'This dataset does not contain Personal Data.',
  };
};

export interface ClassificationTabProps {
  datasetId: string;
  userOwnsDataset: boolean;
  version: any;
  onNotifySuccess: (message: string) => void;
  onNotifyFailure: (message: string) => void;
}

interface ColumnClassification {
  columnName: string;
  classification?: string;
  overrideClassification?: string;
  overrideReason?: string;
  overrideValue?: string;
  overrideReasonValue?: string;
  customReasonText?: string;
  effectiveClassification?: string;
  comment?: string;
}

interface ClassificationData {
  columns: ColumnClassification[];
  reviewStatus?: string;
  datasetClassification?: string;
  overrideClassification?: string;
  overrideReason?: string;
  validationCheckPassed?: boolean;
}

export const ClassificationTab = (props: ClassificationTabProps) => {
  const [classificationData, setClassificationData] = useState<ClassificationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isModifyMode, setIsModifyMode] = useState<boolean>(false);
  const [isSavedState, setIsSavedState] = useState<boolean>(false);
  const [columnFilter, setColumnFilter] = useState<string>('all');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [showBulkEdit, setShowBulkEdit] = useState<boolean>(false);
  const [bulkOverrideValue, setBulkOverrideValue] = useState<string>('');
  const [bulkOverrideReason, setBulkOverrideReason] = useState<string>('');
  const [bulkCustomReason, setBulkCustomReason] = useState<string>('');

  useEffect(() => {
    if (props.version) {
      fetchClassificationData();
    }
  }, [props.datasetId, props.version]);

  const fetchClassificationData = async () => {
    setLoading(true);
    try {
      const apiResponse = await getClassification({ datasetId: props.datasetId });
      const mappedData = mapAPIResponseToUI(apiResponse as any);
      setClassificationData(mappedData);
    } catch (error) {
      console.error('Failed to fetch classification data:', error);
      // Don't show error notification, let the component handle it gracefully
      setClassificationData(null);
    } finally {
      setLoading(false);
    }
  };

  const validateTemplateForPersonalData = async (): Promise<boolean> => {
    try {
      const templates = await listTemplates({
        ownerId: classificationData?.datasetClassification || props.datasetId,
        templateType: TEMPLATE_TYPE_ACCESS_MANAGEMENT,
        resourceId: props.datasetId,
      });
      return templates?.templates?.length > 0;
    } catch (error) {
      console.error('Failed to validate template for personal data:', error);
      return false;
    }
  };

  const handleSaveClassification = async () => {
    setSaving(true);
    try {
      const changedColumns =
        classificationData?.columns
          .filter((col) => {
            const hasClassificationChange = col.overrideValue !== col.classification;
            const hasCommentChange = col.overrideReasonValue?.trim() !== '';

            // Validate: if classification changed, comment is required
            if (hasClassificationChange && !hasCommentChange) {
              throw new Error(`Please provide a reason for overriding classification of column: ${col.columnName}`);
            }

            // Validate: if "Other" is selected, custom text is required
            if (col.overrideReasonValue === 'Other' && !col.customReasonText?.trim()) {
              throw new Error(`Please specify your custom reason for column: ${col.columnName}`);
            }

            return hasClassificationChange || hasCommentChange;
          })
          .map((col) => {
            const result: any = {
              columnName: col.columnName,
            };

            // Always include and convert UI value to backend enum
            if (col.overrideValue !== undefined && col.overrideValue !== null) {
              const mappedValue = col.overrideValue === 'Personal Data' ? 'PERSONAL' : 'NONE';
              result.overriddenClassification = mappedValue;
            }

            // Always include overrideComment if it exists
            const comment =
              col.overrideReasonValue === 'Other' ? col.customReasonText?.trim() : col.overrideReasonValue?.trim();
            if (comment) {
              result.overrideComment = comment;
            }

            return result;
          }) || [];

      const updateInput = { datasetId: props.datasetId, columns: changedColumns };
      const updatePayload = transformUIToAPIFormat(updateInput);

      const updateResponse = await updateClassification(updatePayload);
      const mappedData = mapAPIResponseToUI(updateResponse as any);
      setClassificationData(mappedData);

      // Check if any column has PERSONAL classification and show template warning
      const hasPersonalData = changedColumns.some((col) => col.overriddenClassification === 'PERSONAL');
      if (hasPersonalData && !(await validateTemplateForPersonalData())) {
        props.onNotifyFailure('Template is missing. Please attach an access template.');
      } else {
        props.onNotifySuccess('Data classification overrides saved successfully');
      }

      setHasChanges(false);
      setIsModifyMode(false);
      setIsSavedState(true);
    } catch (error) {
      console.error('Failed to save classification overrides:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save classification overrides';
      props.onNotifyFailure(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getPersonalDataStatus = (classification: string) => {
    if (classification === 'Personal Data') return 'Personal';
    if (classification === 'Not Personal Data') return 'None';
    return 'None';
  };

  const handleAcceptClassification = async () => {
    setSaving(true);
    try {
      const updateInput = {
        datasetId: props.datasetId,
        reviewStatus: 'ACCEPTED',
      };
      const updatePayload = transformUIToAPIFormat(updateInput);
      const updateResponse = await updateClassification(updatePayload);
      const mappedData = mapAPIResponseToUI(updateResponse as any);
      setClassificationData(mappedData);
      props.onNotifySuccess('Classification accepted successfully');
      setIsSavedState(true);
    } catch (error) {
      console.error('Failed to accept classification:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to accept classification';
      props.onNotifyFailure(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleModifyClassification = () => {
    setIsModifyMode(true);
    setIsSavedState(false);
    setColumnFilter('all');
    setSelectedColumns([]);
    setShowBulkEdit(false);
  };

  const handleCancelModify = () => {
    setIsModifyMode(false);
    setHasChanges(false);
    setColumnFilter('all');
    setSelectedColumns([]);
    setShowBulkEdit(false);
    // If we were in saved state before, go back to it
    if (isSavedState) {
      setIsSavedState(true);
    }
    // Reset any changes
    if (classificationData) {
      setClassificationData({
        ...classificationData,
        columns: classificationData.columns.map((col) => ({
          ...col,
          overrideValue: col.classification,
          overrideReasonValue: '',
          customReasonText: '',
        })),
      });
    }
  };

  const handleOverrideValueChange = (item: ColumnClassification, value: string) => {
    setClassificationData((prevData) => {
      if (!prevData) return prevData;
      const updatedData = {
        ...prevData,
        columns: prevData.columns.map((col) => {
          if (col.columnName === item.columnName) {
            return {
              ...col,
              overrideValue: value,
              // Reset comment to default when classification changes
              overrideReasonValue: value !== col.classification ? '' : col.overrideReasonValue,
            };
          }
          return col;
        }),
      };

      const hasAnyChanges = updatedData.columns.some(
        (col) => col.overrideValue !== col.classification || col.overrideReasonValue !== '',
      );
      setHasChanges(hasAnyChanges);

      return updatedData;
    });
  };

  const handleOverrideReasonChange = (item: ColumnClassification, value: string) => {
    // Update the override reason for this column
    setClassificationData((prevData) => {
      if (!prevData) return prevData;
      const updatedData = {
        ...prevData,
        columns: prevData.columns.map((col) =>
          col.columnName === item.columnName
            ? { ...col, overrideReasonValue: value, customReasonText: value === 'Other' ? col.customReasonText : '' }
            : col,
        ),
      };

      // Check if there are any changes from original classification
      const hasAnyChanges = updatedData.columns.some(
        (col) => col.overrideValue !== col.classification || col.overrideReasonValue !== '',
      );
      setHasChanges(hasAnyChanges);

      return updatedData;
    });
  };

  const handleCustomReasonTextChange = (item: ColumnClassification, value: string) => {
    // Update the custom reason text for this column
    setClassificationData((prevData) => {
      if (!prevData) return prevData;
      const updatedData = {
        ...prevData,
        columns: prevData.columns.map((col) =>
          col.columnName === item.columnName ? { ...col, customReasonText: value } : col,
        ),
      };

      // Check if there are any changes from original classification
      const hasAnyChanges = updatedData.columns.some(
        (col) => col.overrideValue !== col.classification || col.overrideReasonValue !== '',
      );
      setHasChanges(hasAnyChanges);

      return updatedData;
    });
  };

  // Get filtered columns based on classification filter
  const getFilteredColumns = () => {
    if (!classificationData) return [];
    let filtered = classificationData.columns;

    if (columnFilter !== 'all') {
      filtered = filtered.filter((col) => {
        const currentClassification = col.overrideValue || col.classification;
        const dataClass = getPersonalDataStatus(currentClassification);
        if (columnFilter === 'personal') {
          return dataClass === 'Personal';
        } else if (columnFilter === 'none') {
          return dataClass === 'None';
        }
        return true;
      });
    }

    return filtered;
  };

  // Handle bulk edit apply
  const handleBulkEditApply = () => {
    if (selectedColumns.length === 0 || !bulkOverrideValue) return;

    setClassificationData((prevData) => {
      if (!prevData) return prevData;
      const updatedData = {
        ...prevData,
        columns: prevData.columns.map((col) => {
          if (selectedColumns.includes(col.columnName)) {
            const comment = bulkOverrideReason === 'Other' ? bulkCustomReason : bulkOverrideReason;
            return {
              ...col,
              overrideValue: bulkOverrideValue,
              overrideReasonValue: comment,
              customReasonText: bulkOverrideReason === 'Other' ? bulkCustomReason : '',
            };
          }
          return col;
        }),
      };

      const hasAnyChanges = updatedData.columns.some(
        (col) => col.overrideValue !== col.classification || col.overrideReasonValue !== '',
      );
      setHasChanges(hasAnyChanges);

      return updatedData;
    });

    // Reset bulk edit state
    setShowBulkEdit(false);
    setBulkOverrideValue('');
    setBulkOverrideReason('');
    setBulkCustomReason('');
    setSelectedColumns([]);
  };

  const getColumnDefinitions = (): TableProps.ColumnDefinition<ColumnClassification>[] => {
    if (isModifyMode) {
      // 6 columns in modify mode (including checkbox)
      return [
        {
          id: 'select',
          header: (
            <Checkbox
              checked={selectedColumns.length === getFilteredColumns().length && getFilteredColumns().length > 0}
              indeterminate={selectedColumns.length > 0 && selectedColumns.length < getFilteredColumns().length}
              onChange={({ detail }) => {
                if (detail.checked) {
                  setSelectedColumns(getFilteredColumns().map((col) => col.columnName));
                } else {
                  setSelectedColumns([]);
                }
              }}
            />
          ),
          cell: (item: ColumnClassification) => (
            <Checkbox
              checked={selectedColumns.includes(item.columnName)}
              onChange={({ detail }) => {
                if (detail.checked) {
                  setSelectedColumns((prev) => [...prev, item.columnName]);
                } else {
                  setSelectedColumns((prev) => prev.filter((name) => name !== item.columnName));
                }
              }}
            />
          ),
          width: '60px',
          minWidth: 60,
        },
        {
          id: 'columnName',
          header: 'Column Name',
          cell: (item: ColumnClassification) => item.columnName,
          width: '18%',
          minWidth: 120,
        },
        {
          id: 'personalData',
          header: 'Data Class',
          cell: (item: ColumnClassification) => getPersonalDataStatus(item.overrideValue || item.classification),
          width: '18%',
          minWidth: 120,
        },
        {
          id: 'comments',
          header: 'Comments',
          cell: (item: ColumnClassification) => {
            // Show override comment if exists, otherwise show original comment
            return item.overrideReasonValue || item.comment || '';
          },
          width: '21%',
          minWidth: 180,
        },
        {
          id: 'overrideValue',
          header: 'Override Value',
          cell: (item: ColumnClassification) => (
            <Select
              options={[
                { label: 'Personal Data', value: 'Personal Data' },
                { label: 'Not Personal Data', value: 'Not Personal Data' },
              ]}
              selectedOption={{
                label: item.overrideValue || 'Not Personal Data',
                value: item.overrideValue || 'Not Personal Data',
              }}
              onChange={(e) => handleOverrideValueChange(item, e.detail.selectedOption.value)}
              expandToViewport
            />
          ),
          width: '21%',
          minWidth: 160,
        },
        {
          id: 'overrideReason',
          header: 'Override Reason',
          cell: (item: ColumnClassification) => (
            <SpaceBetween size='xs' direction='vertical'>
              <Select
                options={[
                  { label: 'Select a reason', value: '' },
                  { label: 'This column is redacted', value: 'This column is redacted' },
                  { label: 'This column is blank', value: 'This column is blank' },
                  { label: 'Other', value: 'Other' },
                ]}
                selectedOption={{
                  label: item.overrideReasonValue || 'Select a reason',
                  value: item.overrideReasonValue || '',
                }}
                onChange={(e) => handleOverrideReasonChange(item, e.detail.selectedOption.value)}
                disabled={item.overrideValue === item.classification}
                invalid={item.overrideValue !== item.classification && !item.overrideReasonValue}
                expandToViewport
              />
              {item.overrideReasonValue === 'Other' && (
                <Input
                  value={item.customReasonText || ''}
                  onChange={(e) => handleCustomReasonTextChange(item, e.detail.value)}
                  placeholder='Please specify your reason...'
                  invalid={item.overrideReasonValue === 'Other' && !item.customReasonText?.trim()}
                />
              )}
            </SpaceBetween>
          ),
          width: '22%',
          minWidth: 200,
        },
      ];
    } else {
      // 3 columns in normal/saved mode
      return [
        {
          id: 'columnName',
          header: 'Column Name',
          cell: (item: ColumnClassification) => item.columnName,
          width: '33.33%',
          minWidth: 200,
        },
        {
          id: 'personalData',
          header: 'Data Class',
          cell: (item: ColumnClassification) =>
            getPersonalDataStatus(item.effectiveClassification || item.overrideValue || item.classification || ''),
          width: '33.33%',
          minWidth: 150,
        },
        {
          id: 'comments',
          header: 'Comments',
          cell: (item: ColumnClassification) => {
            // Show override comment if exists, otherwise show original comment
            return item.overrideReasonValue || item.comment || '';
          },
          width: '33.34%',
          minWidth: 200,
        },
      ];
    }
  };

  if (loading || !props.version) {
    return (
      <Container header={<Header>Dataset Classification</Header>}>
        <Box textAlign='center' padding='l'>
          <Spinner size='large' />
        </Box>
      </Container>
    );
  }

  if (!classificationData) {
    const message = props.userOwnsDataset
      ? 'Classification data is not available for this dataset.'
      : 'You are not the dataset owner. Classification data is not available for you.';

    return (
      <Container header={<Header>Dataset Classification</Header>}>
        <Alert type='info'>{message}</Alert>
      </Container>
    );
  }

  return (
    <SpaceBetween size='m' direction='vertical'>
      <style>
        {`
          .classification-table {
            min-width: 100% !important;
            table-layout: fixed !important;
          }
          .classification-table .awsui-table-cell {
            overflow: visible !important;
            position: relative !important;
            white-space: nowrap !important;
          }
          .classification-table .awsui-table-row {
            overflow: visible !important;
          }
          .classification-table .awsui-table-body {
            overflow: visible !important;
          }
          .classification-table .awsui-table {
            overflow: visible !important;
            width: 100% !important;
            min-width: ${isModifyMode ? '1100px' : '800px'} !important;
          }
          .classification-table .awsui-button-dropdown {
            position: relative !important;
            z-index: 10 !important;
          }
          .classification-table .awsui-button-dropdown[data-open="true"] {
            z-index: 9999 !important;
          }
          .classification-table .awsui-button-dropdown-items {
            z-index: 9999 !important;
            position: absolute !important;
            overflow: visible !important;
          }
          .classification-table .awsui-button-dropdown-items-container {
            z-index: 9999 !important;
            position: absolute !important;
            overflow: visible !important;
          }
        `}
      </style>

      {/* Data Handling Standards Header */}
      <Container
        header={
          <Header
            actions={
              props.userOwnsDataset ? (
                isModifyMode ? (
                  <SpaceBetween size='s' direction='horizontal'>
                    <Button variant='normal' onClick={fetchClassificationData} loading={loading} iconName='refresh' />
                    <Button variant='normal' onClick={handleCancelModify} disabled={saving}>
                      Cancel
                    </Button>
                    <Button
                      variant='primary'
                      onClick={handleSaveClassification}
                      loading={saving}
                      disabled={!hasChanges}
                    >
                      Save
                    </Button>
                  </SpaceBetween>
                ) : isSavedState ? (
                  <SpaceBetween size='s' direction='horizontal'>
                    <Button variant='normal' onClick={fetchClassificationData} loading={loading} iconName='refresh' />
                    <Button variant='normal' onClick={handleModifyClassification}>
                      Modify
                    </Button>
                  </SpaceBetween>
                ) : (
                  <SpaceBetween size='s' direction='horizontal'>
                    <Button variant='normal' onClick={fetchClassificationData} loading={loading} iconName='refresh' />
                    {classificationData.reviewStatus === 'PENDING' && (
                      <Button variant='primary' onClick={handleAcceptClassification} loading={saving}>
                        Accept
                      </Button>
                    )}
                    <Button variant='normal' onClick={handleModifyClassification}>
                      Modify
                    </Button>
                  </SpaceBetween>
                )
              ) : null
            }
          >
            Dataset Classification{' '}
            <Popover
              size='small'
              position='top'
              dismissButton={false}
              triggerType='custom'
              content='Classification API is currently in beta testing phase'
            >
              <span
                style={{ fontSize: '12px', color: '#0972d3', fontWeight: 'bold', marginLeft: '8px', cursor: 'help' }}
              >
                Beta
              </span>
            </Popover>
          </Header>
        }
      >
        <SpaceBetween size='m' direction='vertical'>
          {/* Dataset Classification Status */}
          <Box>
            <span style={{ color: '#374151', fontWeight: 'normal' }}>
              Dataset Classification: {classificationData.datasetClassification}
            </span>
          </Box>
          <Box>
            <span style={{ color: '#374151', fontWeight: 'normal' }}>
              Review Status:{' '}
              {classificationData.reviewStatus === 'ACCEPTED'
                ? 'Accepted'
                : classificationData.reviewStatus === 'OVERRIDDEN'
                ? 'Overridden'
                : 'Pending Review'}
            </span>
          </Box>

          {/* Validation Check Warning */}
          {!classificationData.validationCheckPassed && !isModifyMode && !isSavedState && (
            <Alert type='warning'>
              This dataset may contain Personal Data and failed validation checks for Personal Data. Select "Accept" if
              this dataset contains Personal Data, then associate template and revalidate checks.
            </Alert>
          )}

          {/* Modify Mode Warning */}
          {isModifyMode && (
            <Alert type='info'>
              We will record and audit your data classification override. If we inquire about your override actions, we
              request that you respond within 48 hours to prevent escalations.
            </Alert>
          )}

          {/* Bulk Edit Controls */}
          {isModifyMode && (
            <SpaceBetween size='s' direction='vertical'>
              <SpaceBetween size='s' direction='horizontal'>
                <Select
                  selectedOption={{
                    label:
                      columnFilter === 'all'
                        ? 'All columns'
                        : columnFilter === 'personal'
                        ? 'Personal data columns'
                        : 'Non-personal data columns',
                    value: columnFilter,
                  }}
                  onChange={({ detail }) => {
                    setColumnFilter(detail.selectedOption.value);
                    setSelectedColumns([]);
                  }}
                  options={[
                    { label: 'All columns', value: 'all' },
                    { label: 'Personal data columns', value: 'personal' },
                    { label: 'Non-personal data columns', value: 'none' },
                  ]}
                />
                <Button variant='normal' onClick={() => setShowBulkEdit(true)} disabled={selectedColumns.length === 0}>
                  Bulk edit ({selectedColumns.length})
                </Button>
              </SpaceBetween>

              {showBulkEdit && (
                <Container>
                  <SpaceBetween size='s' direction='vertical'>
                    <Header>Bulk Edit {selectedColumns.length} columns</Header>
                    <SpaceBetween size='s' direction='horizontal'>
                      <Select
                        selectedOption={{
                          label: bulkOverrideValue || 'Select classification',
                          value: bulkOverrideValue,
                        }}
                        onChange={({ detail }) => setBulkOverrideValue(detail.selectedOption.value)}
                        options={[
                          { label: 'Personal Data', value: 'Personal Data' },
                          { label: 'Not Personal Data', value: 'Not Personal Data' },
                        ]}
                        placeholder='Select classification'
                      />
                      <Select
                        selectedOption={{ label: bulkOverrideReason || 'Select reason', value: bulkOverrideReason }}
                        onChange={({ detail }) => setBulkOverrideReason(detail.selectedOption.value)}
                        options={[
                          { label: 'This column is redacted', value: 'This column is redacted' },
                          { label: 'This column is blank', value: 'This column is blank' },
                          { label: 'Other', value: 'Other' },
                        ]}
                        placeholder='Select reason'
                        disabled={!bulkOverrideValue}
                      />
                    </SpaceBetween>
                    {bulkOverrideReason === 'Other' && (
                      <Input
                        value={bulkCustomReason}
                        onChange={({ detail }) => setBulkCustomReason(detail.value)}
                        placeholder='Please specify your reason...'
                      />
                    )}
                    <SpaceBetween size='s' direction='horizontal'>
                      <Button variant='primary' onClick={handleBulkEditApply}>
                        Apply to {selectedColumns.length} columns
                      </Button>
                      <Button variant='normal' onClick={() => setShowBulkEdit(false)}>
                        Cancel
                      </Button>
                    </SpaceBetween>
                  </SpaceBetween>
                </Container>
              )}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>

      {/* Column Classifications Table */}
      <div style={{ overflowX: 'auto', minWidth: '100%' }}>
        <Table
          className='classification-table'
          columnDefinitions={getColumnDefinitions()}
          items={getFilteredColumns()}
          stripedRows
          contentDensity='comfortable'
          variant='full-page'
          header={
            <Header
              counter={classificationData.columns?.length ? `(${classificationData.columns.length})` : '(0)'}
              description={
                isModifyMode
                  ? 'Modify column classifications as needed'
                  : 'Review the recommended classifications for each column'
              }
            >
              Column Classifications
            </Header>
          }
          empty={<EmptyState title='No columns' subtitle='No column classification data available.' />}
        />
      </div>
    </SpaceBetween>
  );
};
